import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const OccupancyDialog = ({
  open, onOpenChange, selectedUnit, occupancyAction,
  onSuccess, submitting, setSubmitting
}) => {
  const [form, setForm] = useState({
    contact: { full_name: '', primary_phone: '', email: '', address: '' },
    contact_id: null,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '', monthly_rent: '', security_deposit: '',
    agreement_reference: '', notes: '',
  });
  const [suggestions, setSuggestions] = useState([]);

  const searchDuplicates = async (phone) => {
    if (!phone || phone.length < 4) { setSuggestions([]); return; }
    try {
      const res = await fetch(`${API_URL}/api/v1/contacts/search-duplicates?phone=${encodeURIComponent(phone)}`, { credentials: 'include' });
      if (res.ok) setSuggestions(await res.json());
    } catch (err) { console.error('Search failed:', err); }
  };

  const selectContact = (c) => {
    setForm(prev => ({
      ...prev, contact_id: c.contact_id,
      contact: { full_name: c.full_name, primary_phone: c.primary_phone, email: c.email || '', address: c.address_line_1 || '' }
    }));
    setSuggestions([]);
  };

  const handleSubmit = async () => {
    if (!form.contact.full_name.trim() || !form.contact.primary_phone.trim()) {
      toast.error('Contact name and phone are required'); return;
    }
    if (!form.start_date) { toast.error('Start date is required'); return; }

    setSubmitting(true);
    try {
      let contactId = form.contact_id;
      if (!contactId) {
        const cRes = await fetch(`${API_URL}/api/v1/contacts`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ full_name: form.contact.full_name, primary_phone: form.contact.primary_phone, email: form.contact.email, address_line_1: form.contact.address }),
        });
        if (!cRes.ok) throw new Error('Failed to create contact');
        contactId = (await cRes.json()).contact_id;
      }

      if (occupancyAction === 'rented') {
        const res = await fetch(`${API_URL}/api/v1/occupancy/units/${selectedUnit.unit_id}/rentals`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            primary_tenant_contact_id: contactId, lease_start_date: form.start_date,
            lease_end_date: form.end_date || null,
            monthly_rent: form.monthly_rent ? parseFloat(form.monthly_rent) : null,
            security_deposit: form.security_deposit ? parseFloat(form.security_deposit) : null,
            agreement_reference: form.agreement_reference || null, notes: form.notes || null,
          }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
        toast.success('Rental record created');
      } else {
        const res = await fetch(`${API_URL}/api/v1/occupancy/units/${selectedUnit.unit_id}/owner-occupancy`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ primary_contact_id: contactId, start_date: form.start_date, end_date: form.end_date || null, notes: form.notes || null }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
        toast.success('Owner occupancy recorded');
      }

      await fetch(`${API_URL}/api/v1/contacts/unit/${selectedUnit.unit_id}/relationships`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: contactId, relationship_type: occupancyAction === 'rented' ? 'tenant' : 'occupying_owner' }),
      });

      onSuccess();
    } catch (error) {
      toast.error(error.message || 'Failed to set occupancy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    if (v) {
      setForm({
        contact: { full_name: '', primary_phone: '', email: '', address: '' },
        contact_id: null, start_date: new Date().toISOString().split('T')[0],
        end_date: '', monthly_rent: '', security_deposit: '', agreement_reference: '', notes: '',
      });
      setSuggestions([]);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{occupancyAction === 'rented' ? 'Set as Rented' : 'Set as Owner Occupied'}</DialogTitle>
          <DialogDescription>{selectedUnit?.unit_number} — {occupancyAction === 'rented' ? 'Record tenant and lease details' : 'Record owner occupancy'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
          <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
            <p className="text-xs font-semibold">{occupancyAction === 'rented' ? 'Tenant Contact' : 'Occupying Owner'} *</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name *</Label>
                <Input value={form.contact.full_name} onChange={(e) => setForm(prev => ({...prev, contact: {...prev.contact, full_name: e.target.value}, contact_id: null}))} className="h-8 text-sm" data-testid="occ-contact-name" />
              </div>
              <div className="space-y-1 relative">
                <Label className="text-xs">Phone *</Label>
                <Input value={form.contact.primary_phone} onChange={(e) => { setForm(prev => ({...prev, contact: {...prev.contact, primary_phone: e.target.value}, contact_id: null})); searchDuplicates(e.target.value); }} className="h-8 text-sm" data-testid="occ-contact-phone" />
                {suggestions.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg max-h-32 overflow-y-auto">
                    <p className="text-[10px] text-muted-foreground px-2 py-1 border-b">Existing contacts:</p>
                    {suggestions.map(c => (
                      <button key={c.contact_id} className="w-full text-left px-2 py-1.5 hover:bg-muted text-xs flex justify-between" onClick={() => selectContact(c)}>
                        <span className="font-medium">{c.full_name}</span>
                        <span className="text-muted-foreground">{c.primary_phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={form.contact.email} onChange={(e) => setForm(prev => ({...prev, contact: {...prev.contact, email: e.target.value}}))} className="h-8 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs">Address</Label><Input value={form.contact.address} onChange={(e) => setForm(prev => ({...prev, contact: {...prev.contact, address: e.target.value}}))} className="h-8 text-sm" /></div>
            </div>
            {form.contact_id && <p className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Linked to existing contact</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">{occupancyAction === 'rented' ? 'Lease Start' : 'Occupancy Start'} *</Label><Input type="date" value={form.start_date} onChange={(e) => setForm(prev => ({...prev, start_date: e.target.value}))} className="h-8 text-sm" data-testid="occ-start-date" /></div>
            <div className="space-y-1"><Label className="text-xs">{occupancyAction === 'rented' ? 'Lease End' : 'End Date'}</Label><Input type="date" value={form.end_date} onChange={(e) => setForm(prev => ({...prev, end_date: e.target.value}))} className="h-8 text-sm" /></div>
          </div>
          {occupancyAction === 'rented' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Monthly Rent (₹)</Label><Input type="number" value={form.monthly_rent} onChange={(e) => setForm(prev => ({...prev, monthly_rent: e.target.value}))} className="h-8 text-sm" placeholder="25000" data-testid="occ-rent" /></div>
                <div className="space-y-1"><Label className="text-xs">Security Deposit (₹)</Label><Input type="number" value={form.security_deposit} onChange={(e) => setForm(prev => ({...prev, security_deposit: e.target.value}))} className="h-8 text-sm" placeholder="50000" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Agreement Reference</Label><Input value={form.agreement_reference} onChange={(e) => setForm(prev => ({...prev, agreement_reference: e.target.value}))} className="h-8 text-sm" placeholder="e.g. RENT-2026-001" data-testid="occ-agreement" /></div>
            </>
          )}
          <div className="space-y-1"><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm(prev => ({...prev, notes: e.target.value}))} rows={2} className="text-sm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="confirm-occupancy">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
